// IT-ASSET-EMPLOYEE-GROUPING-001 (Oct 2026) — "By Employee" view of the IT Asset Inventory.
// Presentation only: groups come from GET /api/it-assets/by-employee (assignment state is the
// source of truth); row actions are the same handlers the Assets view uses (passed in).
import React from 'react';
import { ChevronRight, Users, AlertTriangle, Clock, IdCard } from 'lucide-react';
import './AssetsByEmployee.css';

const Badge = ({ cls, children, testId }) => <span className={cls} data-testid={testId}>{children}</span>;

export const EmployeeAssetsTable = ({ assets, renderActions, testId }) => (
  <table className="data-table assets-table emp-assets-table" data-testid={testId}>
    <thead>
      <tr>
        <th>Asset Tag</th><th>Asset</th><th>Type</th><th>Risk</th><th>Condition</th><th>Status</th><th>Attention</th><th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {assets.map(asset => (
        <tr key={asset.id} data-testid={`emp-asset-row-${asset.id}`}>
          <td className="asset-tag-cell"><code>{asset.asset_tag}</code></td>
          <td className="asset-name">{asset.name}</td>
          <td><Badge cls={`category-badge cat-${asset.category}`}>{asset.asset_type_name || asset.category}</Badge></td>
          <td>{asset.risk ? <Badge cls={`status-badge risk-${asset.risk}`}>{asset.risk}</Badge> : '-'}</td>
          <td><Badge cls={`condition-badge cond-${asset.condition}`}>{asset.condition}</Badge></td>
          <td><Badge cls={`status-badge status-${asset.status}`}>{asset.status?.replace('_', ' ')}</Badge></td>
          <td>
            {asset.attention_reasons?.length ? (
              <span className="emp-attn" title={asset.attention_reasons.join(' · ')} data-testid={`emp-asset-attn-${asset.id}`}>
                {asset.status === 'pending_return' ? <Clock size={12} /> : <AlertTriangle size={12} />}
                {asset.attention_reasons[0]}{asset.attention_reasons.length > 1 ? ` +${asset.attention_reasons.length - 1}` : ''}
              </span>
            ) : <span className="emp-attn-none">—</span>}
          </td>
          <td className="actions-cell">{renderActions(asset)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const AssetsByEmployee = ({ groups, loading, expandedId, onToggle, renderActions, search }) => {
  if (loading) return <div className="emp-empty" data-testid="emp-groups-loading">Loading employee groups…</div>;
  if (!groups.length) {
    return (
      <div className="emp-empty" data-testid="emp-groups-empty">
        <Users size={28} />
        <p>{search ? `No employees or assets match "${search}".` : 'No assets are currently assigned to employees.'}</p>
      </div>
    );
  }
  return (
    <div className="emp-groups" data-testid="emp-groups">
      {groups.map(g => {
        const open = expandedId === g.user_id;
        return (
          <div key={g.user_id} className={`emp-group ${open ? 'open' : ''}`} data-testid={`emp-group-${g.user_id}`} data-asset-count={g.asset_count}>
            <button type="button" className="emp-group-head" onClick={() => onToggle(g.user_id)} aria-expanded={open}
              data-testid={`emp-group-toggle-${g.user_id}`}>
              <span className="emp-avatar">{(g.employee_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</span>
              <span className="emp-main">
                <span className="emp-name" data-testid={`emp-group-name-${g.user_id}`}>{g.employee_name}</span>
                <span className="emp-meta">
                  {g.employee_id && <span className="emp-meta-item"><IdCard size={12} /> {g.employee_id}</span>}
                  {g.team && <span className="emp-meta-item">{g.team}</span>}
                  {g.designation && <span className="emp-meta-item emp-muted">{g.designation}</span>}
                </span>
              </span>
              <span className="emp-count" data-testid={`emp-group-count-${g.user_id}`}>
                <strong>{g.asset_count}</strong> {g.asset_count === 1 ? 'Asset' : 'Assets'}
              </span>
              {g.attention_count > 0 && (
                <span className="emp-attn-pill" title={g.pending_return_count ? `${g.pending_return_count} pending return` : 'Requires attention'}
                  data-testid={`emp-group-attention-${g.user_id}`}>
                  <AlertTriangle size={12} /> {g.attention_count} attention
                </span>
              )}
              <span className="emp-view">{open ? 'Hide Assets' : 'View Assets'} <ChevronRight size={14} className="emp-chev" /></span>
            </button>
            {open && (
              <div className="emp-group-body" data-testid={`emp-group-body-${g.user_id}`}>
                <div className="emp-group-title">{g.employee_name} — {g.asset_count} Assigned {g.asset_count === 1 ? 'Asset' : 'Assets'}</div>
                <div className="table-container">
                  <EmployeeAssetsTable assets={g.assets} renderActions={renderActions} testId={`emp-assets-table-${g.user_id}`} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssetsByEmployee;
